/**
 * providerEndpoints — which backend pair a v2 generation call goes to.
 *
 * There are three identical Lambda pairs: v2-agent-chat / v2-heygen-generate and
 * their twins v2-longvid-chat / v2-longvid-generate and v2-videogen-chat /
 * v2-videogen-generate (same actions, same request and response shapes). A NEW
 * project follows the signed-in user's provider (`video_provider` from
 * get-user-info); an EXISTING project follows the provider stored on its own
 * order, so a routing change never strands a project that is already mid-flight
 * with another provider.
 */
import { API_CONFIG } from './config';
import { useUserStore } from '@/store/userStore';

const E = API_CONFIG.endpoints;

/** 'longvid' is the user-level value; 'longvid_v1' the one on an order row. */
export function isLongvidProvider(provider?: string | null): boolean {
  return provider === 'longvid' || provider === 'longvid_v1';
}

/** 'videogen' is the user-level value; 'videogen_v1' the one on an order row. */
export function isVideogenProvider(provider?: string | null): boolean {
  return provider === 'videogen' || provider === 'videogen_v1';
}

/**
 * Any PROVIDER-ROUTED order — one produced by a twin pair rather than by HeyGen.
 * Every branch that means "not a HeyGen order" hangs off this, so adding a
 * provider never means hunting down another list of `=== 'longvid'` checks.
 */
export function isProviderRouted(provider?: string | null): boolean {
  return isLongvidProvider(provider) || isVideogenProvider(provider);
}

/**
 * `undefined` means the project's provider is not known (a new project, or one
 * that hasn't loaded yet) → the user's provider decides. `null` or '' is a
 * loaded order that stores no provider, i.e. HeyGen — never the user's.
 */
function resolveProvider(projectProvider?: string | null): string | null {
  if (projectProvider !== undefined) return projectProvider;
  return useUserStore.getState().user?.videoProvider ?? null;
}

/** Chat / planner endpoint (messages, approvals, record_event, claim_free_project). */
export function chatEndpoint(projectProvider?: string | null): string {
  const provider = resolveProvider(projectProvider);
  if (isVideogenProvider(provider)) return E.v2VideogenChat;
  return isLongvidProvider(provider) ? E.v2LongvidChat : E.v2AgentChat;
}

/** Generate endpoint (auto orders, generate-from-plan, render poll, preview resume). */
export function generateEndpoint(projectProvider?: string | null): string {
  const provider = resolveProvider(projectProvider);
  if (isVideogenProvider(provider)) return E.v2VideogenGenerate;
  return isLongvidProvider(provider) ? E.v2LongvidGenerate : E.v2HeygenGenerate;
}
